import { useTranslation } from 'react-i18next';
import { Button } from './ui/Button';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { usePronunciationAnalysisOptimized } from '../hooks/usePronunciationAnalysisOptimized';
import type { PronunciationScore } from '../types';

interface PronunciationScorerProps {
  word: string;
  wordId: string;
  sessionId: string;
  onScoreReceived: (score: PronunciationScore) => void;
  currentScore: PronunciationScore | null;
}

export default function PronunciationScorer({
  word,
  wordId,
  onScoreReceived,
  currentScore,
}: PronunciationScorerProps) {
  const { i18n } = useTranslation();
  const { isRecording, audioBlob, startRecording, stopRecording, resetRecording } = useAudioRecorder();
  const { isAnalyzing, error, analyzeRecording } = usePronunciationAnalysisOptimized();

  /*
  useEffect(() => {
    if (audioBlob) {
      handleAnalysis();
    }
  }, [audioBlob]);*/

  const handleAnalysis = async () => {
    const score = await analyzeRecording(audioBlob!, word, wordId);
    if (score) {
      onScoreReceived(score);
    }
  };

  const handleReset = () => {
    resetRecording();
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="pronunciation-scorer bg-white rounded-lg shadow-md p-6 max-w-md mx-auto">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Pronounce: <span className="text-blue-600">{word}</span>
        </h3>
      </div>

      <div className="recording-controls mb-6">
        {!audioBlob ? (
          <div className="text-center">
            <Button
              onClick={isRecording ? stopRecording : startRecording}
              className={`w-24 h-24 rounded-full text-white font-bold flex items-center justify-center mx-auto transition-colors ${
                isRecording ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'
              }`}>
              {isRecording ? 'Stop' : 'Record'}
            </Button>
          </div>
        ) : (
          <div className="text-center">
            <audio src={URL.createObjectURL(audioBlob)} controls className="w-full mb-4" />
            <div className="flex justify-center space-x-4">
              <Button onClick={handleReset} variant="outline">Try Again</Button>
              <Button onClick={handleAnalysis} disabled={isAnalyzing}>
                {isAnalyzing ? <LoadingSpinner /> : 'Analyze'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {error && <div className="text-red-500 text-center mb-4">{error}</div>}

      {currentScore && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Latest Attempt</h3>
          <div className="text-center mb-4">
            <div className={`text-3xl font-bold ${getScoreColor(currentScore.overall)}`}>
              {currentScore.overall}%
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Feedback:</h4>
              <p className="text-sm text-gray-600 bg-gray-50 rounded px-3 py-2">
                {i18n.language === 'vi' ? currentScore.feedback_vi : currentScore.feedback_en}
              </p>
            </div>

            {((currentScore.suggestions_en?.length > 0) || (currentScore.suggestions_vi?.length > 0)) && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Tips:</h4>
                <div className="bg-gray-50 rounded px-3 py-2">
                  <ul className="text-sm text-gray-600 space-y-1">
                    {(i18n.language === 'vi' ? (currentScore.suggestions_vi || []) : (currentScore.suggestions_en || [])).map((suggestion, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-blue-500 mr-2">•</span>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}